import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Database,
  RefreshCw,
  Loader2,
  Sparkles,
  Search,
  Send,
  Layers,
  BookOpen,
  Languages,
  ListChecks,
} from "lucide-react";
import API from "../lib/api";
import AdminGuard from "../components/admin/AdminGuard";
import AdminShell from "../components/admin/AdminShell";
import { errorMessage } from "../lib/errors";

/**
 * Phase 7.4 — Admin KB Management Page
 * --------------------------------------------------------------
 * Surfaces the operational controls for the Ask Donny Knowledge Base:
 *   - Stats: total chunks, total docs, queued-for-ingest, model, store backend
 *   - Breakdown: by topic + by locale
 *   - Action: "Ingest queue" — process all content_items with kb_queued=true
 *   - Debug search: send a free-text query, return top-k chunks with scores
 *
 * The store today is the in-memory cosine implementation; the abstraction is
 * built so a future Atlas Vector Search backend can replace it without changing
 * this page's API contract.
 */
export default function AdminKBPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchTopic, setSearchTopic] = useState("");
  const [searchLocale, setSearchLocale] = useState("");
  const [searchK, setSearchK] = useState(5);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const { data } = await API.get("/admin/kb/stats");
      setStats(data);
    } catch (err) {
      toast.error(errorMessage(err, t("admin.kb.stats_failed", "Couldn\u2019t load KB stats")));
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  const handleIngestQueue = async () => {
    if (ingesting) return;
    setIngesting(true);
    try {
      const { data } = await API.post("/admin/kb/ingest-queue");
      toast.success(
        t("admin.kb.ingest_done", "Ingested {{ok}} / {{total}} queued documents", {
          ok: data.succeeded,
          total: data.queued,
        })
      );
      if (data.failed > 0) {
        toast.warning(t("admin.kb.ingest_some_failed", "{{n}} items failed \u2014 see audit log.", { n: data.failed }));
      }
      await loadStats();
    } catch (err) {
      toast.error(errorMessage(err, t("admin.kb.ingest_failed", "Ingest queue failed")));
    } finally {
      setIngesting(false);
    }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    const q = (searchQuery || "").trim();
    if (!q) {
      toast.error(t("admin.kb.search_empty", "Enter a query to search"));
      return;
    }
    setSearching(true);
    try {
      const payload = { query: q, k: searchK };
      if (searchTopic) payload.topic = searchTopic;
      if (searchLocale) payload.locale = searchLocale;
      const { data } = await API.post("/admin/kb/search", payload);
      setSearchResults(data);
    } catch (err) {
      toast.error(errorMessage(err, t("admin.kb.search_failed", "Search failed")));
    } finally {
      setSearching(false);
    }
  };

  const total = (stats && stats.total_chunks) || 0;
  const docs = (stats && stats.total_documents) || 0;
  const queued = (stats && stats.queued_for_ingest) || 0;
  const byTopic = (stats && stats.by_topic) || {};
  const byLocale = (stats && stats.by_locale) || {};

  return (
    <AdminGuard>
      <AdminShell active="kb">
        <div className="space-y-6" data-testid="admin-kb">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow mb-2">{t("admin.kb.eyebrow", "\u2014 Ask Donny Knowledge Base")}</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight" data-testid="admin-kb-title">
                {t("admin.kb.title", "Knowledge Base")}
              </h2>
              <p className="text-sm text-white/65 mt-2 max-w-2xl leading-relaxed">
                {t(
                  "admin.kb.subtitle",
                  "Ingest your imported content into the Ask Donny knowledge base. Donny will retrieve and cite the most relevant DONiTSH!T library passages, and clearly note when it\u2019s giving general guidance instead."
                )}
              </p>
            </div>
            <button
              onClick={loadStats}
              disabled={loadingStats}
              className="btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="kb-refresh-btn"
            >
              {loadingStats ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t("admin.kb.refresh", "Refresh")}
            </button>
          </div>

          {/* Stats grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Layers}
              label={t("admin.kb.stat_chunks", "Total chunks")}
              value={total.toLocaleString()}
              testid="kb-stat-chunks"
            />
            <StatCard
              icon={BookOpen}
              label={t("admin.kb.stat_docs", "Documents")}
              value={docs.toLocaleString()}
              testid="kb-stat-docs"
            />
            <StatCard
              icon={ListChecks}
              label={t("admin.kb.stat_queued", "Queued for ingest")}
              value={queued.toLocaleString()}
              accent={queued > 0}
              testid="kb-stat-queued"
            />
            <StatCard
              icon={Database}
              label={t("admin.kb.stat_store", "Vector store")}
              value={(stats && stats.store) || "—"}
              subValue={(stats && stats.embed_model) || ""}
              testid="kb-stat-store"
            />
          </div>

          {/* Ingest queue action */}
          {queued > 0 && (
            <div className="card-surface p-5 border border-[#C8A24A]/25" data-testid="kb-ingest-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <Sparkles className="w-4 h-4 gold-text" />
                    {t("admin.kb.ingest_title", "{{n}} documents queued for KB ingestion", { n: queued })}
                  </h3>
                  <p className="text-sm text-white/65 mt-1 max-w-2xl leading-relaxed">
                    {t(
                      "admin.kb.ingest_help",
                      "Each document will be chunked on heading boundaries, embedded via OpenAI text-embedding-3-small, and stored so Donny can cite them in real time."
                    )}
                  </p>
                </div>
                <button
                  onClick={handleIngestQueue}
                  disabled={ingesting}
                  className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="kb-ingest-btn"
                >
                  {ingesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {ingesting ? t("admin.kb.ingest_in_progress", "Ingesting\u2026") : t("admin.kb.ingest_btn", "Ingest queue")}
                </button>
              </div>
            </div>
          )}

          {/* Topic / locale breakdowns */}
          <div className="grid lg:grid-cols-2 gap-4">
            <BreakdownCard
              icon={BookOpen}
              title={t("admin.kb.by_topic", "Chunks by topic")}
              entries={byTopic}
              empty={t("admin.kb.no_topics", "No topics yet \u2014 ingest some content first.")}
              testid="kb-breakdown-topic"
            />
            <BreakdownCard
              icon={Languages}
              title={t("admin.kb.by_locale", "Chunks by locale")}
              entries={byLocale}
              empty={t("admin.kb.no_locales", "No locales yet.")}
              testid="kb-breakdown-locale"
            />
          </div>

          {/* Debug search */}
          <div className="card-surface p-5" data-testid="kb-search-card">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 gold-text" />
              <h3 className="font-display text-lg">{t("admin.kb.search_title", "Debug retrieval")}</h3>
            </div>
            <p className="text-xs text-white/55 mb-4">
              {t(
                "admin.kb.search_help",
                "Test what Donny will retrieve for a given question. Useful for quality-checking after a fresh ingest."
              )}
            </p>
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("admin.kb.search_placeholder", "Try: 'How do I improve my sleep with a morning routine?'")}
                  className="flex-1 min-w-[260px] bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40"
                  data-testid="kb-search-input"
                />
                <select
                  value={searchTopic}
                  onChange={(e) => setSearchTopic(e.target.value)}
                  className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 min-w-[160px]"
                  data-testid="kb-search-topic"
                >
                  <option value="">{t("admin.kb.any_topic", "Any topic")}</option>
                  {Object.keys(byTopic).filter((x) => x !== "(no topic)").sort().map((tp) => (
                    <option key={tp} value={tp}>{tp}</option>
                  ))}
                </select>
                <select
                  value={searchLocale}
                  onChange={(e) => setSearchLocale(e.target.value)}
                  className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 min-w-[120px]"
                  data-testid="kb-search-locale"
                >
                  <option value="">{t("admin.kb.any_locale", "Any locale")}</option>
                  {Object.keys(byLocale).sort().map((l) => (
                    <option key={l} value={l}>{l.toUpperCase()}</option>
                  ))}
                </select>
                <select
                  value={searchK}
                  onChange={(e) => setSearchK(Number(e.target.value))}
                  className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C8A24A]/40 w-[90px]"
                  data-testid="kb-search-k"
                >
                  {[3, 5, 10, 15].map((n) => (
                    <option key={n} value={n}>{t("admin.kb.top_k", "Top {{n}}", { n })}</option>
                  ))}
                </select>
                <button
                  type="submit"
                  disabled={searching || !searchQuery.trim() || total === 0}
                  className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="kb-search-btn"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {t("admin.kb.search_btn", "Search")}
                </button>
              </div>
            </form>

            {searchResults && (
              <div className="mt-5 space-y-3" data-testid="kb-search-results">
                {(searchResults.results || []).length === 0 ? (
                  <div className="text-sm text-white/55 text-center py-6" data-testid="kb-search-empty">
                    {t("admin.kb.search_no_results", "No matching chunks found.")}
                  </div>
                ) : (
                  (searchResults.results || []).map((r) => (
                    <div
                      key={r.chunk_id}
                      className="rounded-lg border border-white/10 bg-black/30 p-4"
                      data-testid={`kb-search-result-${r.rank}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/55 mb-0.5">
                            <span className="gold-text font-mono">#{r.rank}</span>
                            <span>·</span>
                            <span className="gold-text font-mono">{(r.score * 100).toFixed(0)}%</span>
                            {r.topic && (<><span>·</span><span>{r.topic}</span></>)}
                            {r.locale && (<><span>·</span><span>{r.locale.toUpperCase()}</span></>)}
                          </div>
                          <div className="font-medium text-white truncate">{r.title}</div>
                          {Array.isArray(r.heading_path) && r.heading_path.length > 0 && (
                            <div className="text-[11px] text-white/50 truncate mt-0.5">
                              {r.heading_path.join(" \u203a ")}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-white/75 leading-relaxed line-clamp-4">{r.snippet}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </AdminShell>
    </AdminGuard>
  );
}

const StatCard = ({ icon: Icon, label, value, subValue, accent, testid }) => (
  <div
    className={`card-surface p-4 ${accent ? "border border-[#C8A24A]/30" : ""}`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
      <Icon className="w-3.5 h-3.5 gold-text" />
      {label}
    </div>
    <div className={`font-display text-2xl ${accent ? "gold-text" : "text-white"}`}>{value}</div>
    {subValue && <div className="text-[10px] text-white/45 mt-1 font-mono">{subValue}</div>}
  </div>
);

const BreakdownCard = ({ icon: Icon, title, entries, empty, testid }) => {
  const keys = Object.keys(entries || {});
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 gold-text" />
        <h3 className="font-display text-base">{title}</h3>
      </div>
      {keys.length === 0 ? (
        <div className="text-sm text-white/55">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {keys.sort((a, b) => (entries[b] || 0) - (entries[a] || 0)).map((k) => (
            <li key={k} className="flex justify-between items-center text-sm">
              <span className="text-white/80 truncate">{k}</span>
              <span className="text-white/55 font-mono text-xs">{entries[k]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
